import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

/**
 * GWP (gift-with-purchase) auto-add reconciler — plan
 * tasks/integrations/2026-09-01-lg-gwp-auto-add.md (ADR-0023).
 *
 * The server (`snippets/lg-cart-gwp.liquid`) computes the DESIRED gift state per
 * offer on every render/morph and emits `<lg-cart-gwp-guard>` carrying the delta
 * ONLY when the rendered state differs from the desired state. This element
 * executes that delta against the Ajax cart, then resolves ONE
 * CartLinesUpdateEvent so `cart-items-component` performs the single morph
 * (horizon-mechanics §4.15). The re-rendered fragment carries no guard → the loop
 * terminates (generalized `lg-cart-samples` guard, §4.5 / §4.43).
 *
 * CONVERGENCE INVARIANT (≤2 mutations per pass, §5.16):
 *   1. ONE batch /cart/update.js by KEY for all trims/removals (update.js has no
 *      `properties` param → no overwrite hazard; key addressing is mandatory
 *      because a gift variant can coexist as a paid manual line AND a `_gwp`
 *      line, §4.14).
 *   2. AT MOST one /cart/add.js with `properties:{_gwp:<handle>}` for the
 *      remainder — gated by schedule window (Date.now — Liquid `now` is
 *      CDN-cache-stale), removal suppression, and the drift latch.
 *   The mounted `cart-items-component` gets `cart-items-disabled` for the flight.
 *   NOTE — that class is styled DRAWER-ONLY: every rule for it is scoped
 *   `.lg-cart-drawer …` (assets/lg-cart-drawer.css:610-613, :1269-1271), so on the
 *   /cart PAGE it is inert and row controls stay interactive for the flight. It does
 *   NOT close the §4.14-addendum stale window there, as previously claimed. `aria-busy`
 *   applies on both surfaces, and a delta arriving mid-flight is no longer stranded
 *   (see #retryLater), so the page-side gap is cosmetic rather than a correctness hole
 *   — but it is recorded honestly here rather than implying a guard that does not exist.
 *   Follow-up: add a page-scoped rule, or move the existing rules up a scope.
 *
 * TWO session latches (sessionStorage, owner-decided session scope):
 *   - `lg-gwp-suppressed:<offer>` — set when the shopper removes a `_gwp` line
 *     (capture-phase remove-click + QuantitySelectorUpdateEvent→0, identified via
 *     the `<lg-cart-gwp-map>` carrier since underscore props are invisible in row
 *     markup, §4.43). Blocks ADD deltas only.
 *   - `lg-gwp-swept:<offer>` — set when this offer's own add gets swept un-zeroed
 *     (the platform never discounted it → drift). Terminates the add→sweep
 *     oscillation the cooldown alone would only pace (§5.16). Blocks ADD deltas.
 *   BOTH latches block ADDs only; trims/removals ALWAYS execute (fail-closed).
 *
 * Inert under `Shopify.designMode` (§4.16 — no cart mutations while the merchant
 * edits). With JS disabled the guard never runs: no gift is auto-added, nothing
 * errors (AC 8).
 */

const RETRY_COOLDOWN_MS = 3000;
const DISABLED_CLASS = 'cart-items-disabled';
const SUPPRESS_PREFIX = 'lg-gwp-suppressed:';
const SWEPT_PREFIX = 'lg-gwp-swept:';

/** Per-signature cooldown — a repeated identical delta (server keeps returning the same violation) is paced. @type {Map<string, number>} */
const lastAttempt = new Map();

/** Guards against re-entrancy while a pass is in flight (single mount per page, §4.43). */
let inFlight = false;

/* ---- session-latch helpers (fail-safe if sessionStorage is unavailable) ---- */

/** @param {string} key @returns {boolean} */
function latchIsSet(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** @param {string} key */
function latchSet(key) {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* private mode / quota — degrade to no-latch (cooldown still paces) */
  }
}

/** @param {string} handle @returns {boolean} */
function isSuppressed(handle) {
  return latchIsSet(SUPPRESS_PREFIX + handle);
}

/** @param {string} handle @returns {boolean} */
function isSwept(handle) {
  return latchIsSet(SWEPT_PREFIX + handle);
}

/**
 * Reads `<lg-cart-gwp-map>` → { lineKey: offerHandle }. The carrier is emitted on
 * EVERY render (converged included), inside the morph target, so a document-level
 * listener can identify `_gwp` rows without touching invisible row properties.
 * @returns {Record<string, string>}
 */
function readGwpMap() {
  /** @type {Record<string, string>} */
  const map = {};
  document.querySelectorAll('lg-cart-gwp-map').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    (el.dataset.map || '')
      .split('§')
      .filter(Boolean)
      .forEach((pair) => {
        // lastIndexOf, NOT indexOf: a cart line key is `<variant_id>:<hash>` and so
        // contains a colon itself (docs/cart-line-identity-contract.md §1). Splitting on
        // the FIRST colon yielded {"40021542": "abc123:gwp-offer"}, so every lookup by
        // full line key missed and BOTH session latches silently never fired — the gift
        // came back after a shopper removed it (AC 3) and the add→sweep oscillation had
        // no terminator (AC 6). #parseUpdates below already does this correctly.
        // Offer handles are slugs and cannot contain ':', so the last one is the split.
        const idx = pair.lastIndexOf(':');
        if (idx === -1) return;
        const key = pair.slice(0, idx);
        const handle = pair.slice(idx + 1);
        if (key && handle) map[key] = handle;
      });
  });
  return map;
}

/**
 * Collects the SRA section ids to re-render (same walk as
 * component-cart-items.js updateQuantity / lg-cart-subscribe sectionsParam).
 * @returns {string}
 */
function sectionsParam() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

/**
 * Current cart item count from the machinery contract's hidden [ref="cartItemCount"]
 * — used on the add-resolve and error paths so the header bubble keeps its count
 * (cart-icon.js:60 falls back cart → detail.itemCount → 0). Mirrors
 * lg-cart-subscribe.js currentItemCount().
 * @returns {number | undefined}
 */
function currentItemCount() {
  const text = document.querySelector('[ref="cartItemCount"]')?.textContent;
  const count = text ? parseInt(text, 10) : NaN;
  return Number.isFinite(count) ? count : undefined;
}

/**
 * Parses the post-mutation item count out of a section fragment's
 * [ref="cartItemCount"] (component-cart-items.js:264-266 pattern) for the
 * refetch-free add resolve (§4.18 addendum).
 * @param {Record<string, string> | undefined} sections
 * @param {string} sectionsCsv
 * @returns {number | undefined}
 */
function itemCountFromSections(sections, sectionsCsv) {
  const first = sectionsCsv.split(',')[0];
  const html = sections?.[first];
  if (!html) return undefined;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const text = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
  const count = text ? parseInt(text, 10) : NaN;
  return Number.isFinite(count) ? count : undefined;
}

/**
 * Dispatches the LG a11y announce seam (§4.34 / AC 9). The polite region +
 * document-level `lg:cart:announce` listener live in `assets/lg-cart-announce.js`.
 * The copy is fully-formed and locale-driven — the shared snippet renders it as
 * data-msg-* on the guard (the snippet knows the gift title; the JS does not) — so
 * this helper just pipes the chosen message through. The message-selection logic
 * is inline in #reconcile (the announce branch after the resolve).
 * @param {string} message
 */
function announce(message) {
  if (!message) return;
  // Deferred by a macrotask ON PURPOSE. The reconciler dispatches this immediately after
  // deferred.resolve(...), but the consumer's morph runs in a later microtask — and on the
  // /cart PAGE the polite region lives INSIDE the morph target, so a synchronous dispatch
  // wrote text that the server's empty role="status" node then overwrote, and the shopper
  // heard nothing (AC 9 on the page surface). The drawer was unaffected because its region
  // sits outside the hydration key. setTimeout lands after the morph on both surfaces; the
  // region is polite, so the extra tick costs nothing.
  window.setTimeout(() => {
    document.dispatchEvent(new CustomEvent('lg:cart:announce', { detail: { message } }));
  }, 0);
}

class LgCartGwpGuard extends HTMLElement {
  // Morphs UPDATE a persisting guard in place rather than re-inserting it
  // (§4.43 / lg-cart-samples.js:25-36), so connectedCallback alone would strand a
  // changed delta until full reload — re-attempt when any delta attribute changes.
  static observedAttributes = [
    'data-updates',
    'data-add-variant-ids',
    'data-add-qtys',
    'data-add-handles',
    // Schedule bounds too (Code-W1): a promo going live/expiring while a shopper
    // sits on an open /cart re-emits identical variant/qty/handle but CHANGED
    // starts_at/ends_at — without observing these the in-place morph never
    // re-fires the now-in-window add.
    'data-add-starts',
    'data-add-ends',
  ];

  /** @type {number | null} pending #retryLater timer, at most one. */
  #retryTimer = null;

  connectedCallback() {
    this.#attempt();
  }

  disconnectedCallback() {
    // The guard normally persists across morphs (updated in place), but clear the timer
    // on the paths where it is genuinely removed — drawer teardown, template swap — so a
    // wake never fires against a detached node.
    if (this.#retryTimer !== null) {
      window.clearTimeout(this.#retryTimer);
      this.#retryTimer = null;
    }
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.#attempt();
  }

  #attempt() {
    // §4.16 — never mutate the cart while the merchant edits in the theme editor.
    if (window.Shopify?.designMode) return;

    const updates = this.#parseUpdates();
    const adds = this.#parseAdds();
    if (!updates.length && !adds.length) return;

    // Per-signature cooldown (§5.16): the full delta is the signature.
    const signature = JSON.stringify({ updates, adds });
    const now = Date.now();
    // Order matters. Stamping the cooldown before the in-flight bail stranded any delta
    // that arrived DURING a fetch: the signature was recorded, the attempt was then
    // dropped, and nothing retried it — morphs update this guard IN PLACE, so an
    // unchanged attribute set never re-fires attributeChangedCallback. The gift silently
    // failed to add, or a paid _gwp line survived. Bail first, then re-arm.
    if (inFlight) {
      this.#retryLater(RETRY_COOLDOWN_MS);
      return;
    }

    const previous = lastAttempt.get(signature);
    if (previous !== undefined && now - previous < RETRY_COOLDOWN_MS) {
      this.#retryLater(RETRY_COOLDOWN_MS - (now - previous));
      return;
    }
    lastAttempt.set(signature, now);

    this.#reconcile(updates, adds);
  }

  /**
   * Re-attempt once after `delay` ms. A single pending timer only — the guard re-reads
   * its CURRENT attributes on wake, so a later delta supersedes an earlier one and
   * queueing several would just repeat the same read. Cleared on disconnect.
   * @param {number} delay
   */
  #retryLater(delay) {
    if (this.#retryTimer !== null) return;
    this.#retryTimer = window.setTimeout(() => {
      this.#retryTimer = null;
      if (this.isConnected) this.#attempt();
    }, Math.max(delay, 0));
  }

  /**
   * @returns {{ key: string, quantity: number }[]}
   */
  #parseUpdates() {
    return (this.dataset.updates || '')
      .split('§')
      .filter(Boolean)
      .map((pair) => {
        const idx = pair.lastIndexOf(':');
        const key = pair.slice(0, idx);
        const quantity = parseInt(pair.slice(idx + 1), 10);
        return { key, quantity: Number.isFinite(quantity) ? quantity : 0 };
      })
      .filter((u) => u.key);
  }

  /**
   * Parses the parallel add arrays and filters by schedule window + both latches.
   * @returns {{ variantId: number, quantity: number, handle: string }[]}
   */
  #parseAdds() {
    const variantIds = (this.dataset.addVariantIds || '').split('§').filter(Boolean);
    const qtys = (this.dataset.addQtys || '').split('§').filter(Boolean);
    const handles = (this.dataset.addHandles || '').split('§').filter(Boolean);
    // starts/ends arrays are '§'-joined and may contain empty slots (open-ended).
    const starts = (this.dataset.addStarts || '').split('§');
    const ends = (this.dataset.addEnds || '').split('§');

    const nowMs = Date.now();
    /** @type {{ variantId: number, quantity: number, handle: string }[]} */
    const out = [];
    for (let i = 0; i < variantIds.length; i += 1) {
      const handle = handles[i] || '';
      const variantId = Number(variantIds[i]);
      const quantity = parseInt(qtys[i] || '0', 10);
      if (!handle || !Number.isFinite(variantId) || variantId <= 0 || quantity <= 0) continue;

      // Schedule window (§lg-offer-match; Date.now, never CDN-stale Liquid `now`).
      const startRaw = (starts[i] || '').trim();
      const endRaw = (ends[i] || '').trim();
      if (startRaw && Date.parse(startRaw) > nowMs) continue;
      if (endRaw && Date.parse(endRaw) < nowMs) continue;

      // Latches block ADDs only.
      if (isSuppressed(handle)) continue;
      if (isSwept(handle)) continue;

      out.push({ variantId, quantity, handle });
    }
    return out;
  }

  /**
   * @param {{ key: string, quantity: number }[]} updates
   * @param {{ variantId: number, quantity: number, handle: string }[]} adds
   */
  async #reconcile(updates, adds) {
    inFlight = true;
    const host = document.querySelector('cart-items-component');
    // In-flight lock (§4.14 addendum) — dims + blocks controls via CSS keyed on
    // the class; self-heals across the morph. aria-busy marks the flight for AT.
    if (host instanceof HTMLElement) {
      host.classList.add(DISABLED_CLASS);
      host.setAttribute('aria-busy', 'true');
    }

    const sections = sectionsParam();
    // Snapshot handles-in-flight so a drift check post-add can latch the right offer.
    const addHandles = adds.map((a) => a.handle);

    // ONE dispatched event per pass. action:'update' (never 'add') keeps the §4.7
    // auto-open gate cold — a background reconcile must never pop the drawer.
    const deferred = CartLinesUpdateEvent.createPromise();
    const eventLines = [
      ...updates.map((u) => ({ id: u.key, quantity: u.quantity })),
      ...adds.map((a) => ({ merchandiseId: String(a.variantId), quantity: a.quantity })),
    ];
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: eventLines,
        promise: deferred.promise,
      })
    );

    try {
      /** @type {any} */
      let lastData = null;
      /** @type {'update' | 'add'} */
      let lastKind = 'update';

      // --- 1. batch update.js (trims/removals by KEY) ---
      if (updates.length) {
        const updatesMap = Object.fromEntries(updates.map((u) => [u.key, u.quantity]));
        const body = JSON.stringify({
          updates: updatesMap,
          sections,
          sections_url: window.location.pathname,
        });
        const res = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
        lastData = await res.json(); // throws on HTML 429 challenge → catch (§5.16)
        lastKind = 'update';
      }

      // --- 2. at most one add.js with the _gwp property (§4.18: ONE batch POST) ---
      // Multiple offers ⇒ multiple items in a single call; GWP steady state is one
      // variant per offer. Each item carries its OWN `properties._gwp` — the
      // /cart/add.js `items[]` shape accepts per-item `properties`, so distinct
      // offers get distinct handles without a shared-property collision.
      if (adds.length) {
        const body = JSON.stringify({
          items: adds.map((a) => ({
            id: a.variantId,
            quantity: a.quantity,
            properties: { _gwp: a.handle },
          })),
          sections,
          sections_url: window.location.pathname,
        });
        const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
        lastData = await res.json(); // throws on HTML challenge → catch
        lastKind = 'add';
      }

      if (!lastData) {
        // Nothing executed (all adds filtered, no updates) — resolve to current truth.
        deferred.resolve({
          cart: null,
          detail: { itemCount: currentItemCount(), source: 'lg-cart-gwp', didError: false },
        });
        return;
      }

      // --- Resolve (§4.15) ---
      if (lastKind === 'add') {
        // add.js body is NOT a full cart (§4.18 addendum) — NEVER
        // createCartFromAjaxResponse on it. Its `sections` VALUES are full
        // post-mutation renders → morph from detail.sections, bubble from
        // detail.itemCount parsed out of the fragment.
        const count = itemCountFromSections(lastData.sections, sections) ?? currentItemCount();
        deferred.resolve({
          cart: null,
          detail: { sections: lastData.sections, itemCount: count, source: 'lg-cart-gwp', didError: false },
        });
      } else {
        // update.js body IS a full cart — resolve WITH detail.sections (single morph).
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(lastData),
          detail: {
            sections: lastData.sections,
            items: lastData.items,
            itemCount: lastData.item_count,
            source: 'lg-cart-gwp',
            didError: false,
          },
        });
      }

      // --- Announcements (§4.34 / AC 9) ---
      // Copy is locale-driven, rendered as data-msg-* on this guard by the shared
      // snippet (the snippet knows the gift title; the JS does not). Precedence
      // (A11y-W1): an add announces "gift added" (with title); else a full removal
      // (any update to qty 0) announces "gift removed"; else a partial trim (any
      // update to a NON-ZERO qty — the 2×→1× eligibility drop) announces "gift
      // quantity updated" — WITHOUT this branch a partial trim was silent to SRs,
      // violating AC 2/AC 9 intent. Skip silently when the attribute is absent.
      if (adds.length && this.dataset.msgAdded) {
        announce(this.dataset.msgAdded);
      } else if (updates.some((u) => u.quantity === 0) && this.dataset.msgRemoved) {
        announce(this.dataset.msgRemoved);
      } else if (updates.some((u) => u.quantity > 0) && this.dataset.msgReduced) {
        announce(this.dataset.msgReduced);
      }

      // --- Drift latch (§5.16 / AC 6) ---
      // After the add resolves, if the freshly-added _gwp line is still
      // un-discounted server truth, the metaobject/BXGY pair disagree. The NEXT
      // render's sweep will remove it; latch the offer now so its re-add is blocked
      // for the session — terminating the add→sweep oscillation. We can't cheaply
      // read allocations from the add.js body, so we latch pre-emptively on the
      // presence of an add whose handle then appears un-allocated on the next map.
      // Concretely: schedule a one-shot check after the morph settles.
      if (addHandles.length) this.#scheduleDriftLatch(addHandles);
    } catch (error) {
      // HTML challenge (§5.16) or network failure → mirror lg-cart-subscribe.js
      // error resolve (§4.15 addendum): sections-absent so the vendor cache:false
      // truth-restore refetch runs; itemCount keeps the header bubble alive.
      deferred.resolve({
        cart: null,
        detail: { itemCount: currentItemCount(), source: 'lg-cart-gwp', didError: true },
      });
      console.error('lg-cart-gwp-guard: reconcile failed', error);
    } finally {
      inFlight = false;
      const h = document.querySelector('cart-items-component');
      if (h instanceof HTMLElement) {
        h.classList.remove(DISABLED_CLASS);
        h.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * One-shot post-morph drift check: if an add's offer handle still maps to a
   * `_gwp` line that the NEXT server render marks un-discounted, that render's
   * sweep removes it and we latch the offer so it never re-adds this session.
   * The sweep + latch is what breaks the oscillation the cooldown only paces.
   * Implementation: observe the cart section for the next render, then latch any
   * handle whose add we just performed if the fresh guard carries a removal for
   * one of THIS offer's lines (server said drift).
   * @param {string[]} handles
   */
  #scheduleDriftLatch(handles) {
    const target = document.querySelector('cart-items-component');
    if (!(target instanceof HTMLElement)) return;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      observer.disconnect();
    };
    const observer = new MutationObserver(() => {
      if (done) return;
      const map = readGwpMap();
      const guard = document.querySelector('lg-cart-gwp-guard');
      const sweepUpdates =
        guard instanceof HTMLElement
          ? (guard.dataset.updates || '').split('§').filter(Boolean)
          : [];
      // Any key mapped to one of our just-added handles that the fresh guard is
      // zeroing = the platform never discounted it → drift. Latch the offer.
      handles.forEach((handle) => {
        const driftedKey = Object.keys(map).find(
          (k) => map[k] === handle && sweepUpdates.includes(`${k}:0`)
        );
        if (driftedKey) latchSet(SWEPT_PREFIX + handle);
      });
      finish();
    });
    observer.observe(target, { childList: true, subtree: true });
    // Safety: stop observing after a bounded window.
    setTimeout(finish, 4000);
  }
}

/* ---- Suppression listeners (document-level; rows are morph-replaced) ---- */
// A shopper removing a `_gwp` line must suppress that offer for the session. The
// row's `_gwp`-ness is read from the map carrier (invisible properties, §4.43).

/** @param {string} lineKey @returns {string | undefined} */
function handleForRowKey(lineKey) {
  if (!lineKey) return undefined;
  return readGwpMap()[lineKey];
}

/** @param {Element | null} el @returns {string} */
function rowKey(el) {
  const row = el?.closest?.('[data-key]');
  return row instanceof HTMLElement ? row.dataset.key || '' : '';
}

// Capture-phase remove-click: read the row BEFORE the morph wipes it.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const removeBtn = target?.closest?.('[on\\:click^="/onLineItemRemove"]');
    if (!removeBtn) return;
    const handle = handleForRowKey(rowKey(removeBtn));
    if (handle) latchSet(SUPPRESS_PREFIX + handle);
  },
  true
);

// Quantity → 0 is also a removal (steppers set value programmatically; the custom
// event covers +/- and typed-then-blur, cf. lg-cart-announce.js).
document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const detail = /** @type {{ quantity?: number }} */ (
    /** @type {CustomEvent} */ (event).detail || {}
  );
  if (detail.quantity !== 0) return;
  const target = event.target instanceof Element ? event.target : null;
  const handle = handleForRowKey(rowKey(target));
  if (handle) latchSet(SUPPRESS_PREFIX + handle);
});

if (!customElements.get('lg-cart-gwp-guard')) {
  customElements.define('lg-cart-gwp-guard', LgCartGwpGuard);
}
